import { DeviceType } from '@services/constants'
import {
  ChargingPointDetailStatusColor,
  EStatusPill,
  IStatisticalByDeviceType,
  useSessionDeviceStatistical,
} from '@services/device'
import { useParkingLotList } from '@services/operation'
import { toThousandSeparator } from '@shared/innmaxLib/utils/webHelper'
import { Col, Row } from 'antd'
import moment from 'moment'
import { pluck, sum } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useDashboardContext } from '../../Provider'

type DevicesBoardType = {
  lastUpdated: moment.Moment
  onSelect?: () => void
}

export default function SimpleDevicesBoard({
  lastUpdated,
  onSelect,
}: DevicesBoardType) {
  const { t } = useTranslation()

  const { refetch, isLoading, data } = useSessionDeviceStatistical()

  const { dataSource: parkingLotData } = useParkingLotList(
    {
      pageNum: 0,
      pageSize: 1,
    },
    {
      keepPreviousData: true,
    }
  )

  const { baseLayer, setFilterVariables, filterVariables } =
    useDashboardContext()

  React.useEffect(() => {
    if (!baseLayer?.id) {
      return
    }
    refetch({
      sessionId: baseLayer?.id,
      deep: true,
      isPositioned: true,
      cacheKey: 'deviceBoard',
    })
  }, [lastUpdated, baseLayer]) //eslint-disable-line

  const sumConnectorDetail = (...keys: (keyof IStatisticalByDeviceType)[]) => {
    const connectorData =
      data?.filter(item => item?.deviceType === DeviceType.CONNECTOR) || []
    const total = keys.reduce((acc, key) => {
      return acc + sum(pluck(key as any, connectorData))
    }, 0)
    return toThousandSeparator(total)
  }

  const chargingpointCount = toThousandSeparator(
    sum(
      pluck(
        'deviceCount',
        data?.filter(item => item.deviceType === DeviceType.CHARGINGPOINT) || []
      )
    )
  )

  return (
    <StyledBoard>
      <div className="flex flex-col gap-5">
        {[
          {
            title: '充電樁數',
            value: chargingpointCount,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
          {
            title: '可使用',
            value: sumConnectorDetail('availableCount'),
            backgroundColor:
              ChargingPointDetailStatusColor[EStatusPill.Available],
          },
          {
            title: '充電中',
            value: sumConnectorDetail('chargingCount'),
            backgroundColor:
              ChargingPointDetailStatusColor[EStatusPill.Charging],
          },
          {
            title: '故障',
            value: sumConnectorDetail('faultedCount'),
            backgroundColor:
              ChargingPointDetailStatusColor[EStatusPill.Faulted],
          },
          {
            title: '占用中',
            value: sumConnectorDetail('finishingCount'),
            backgroundColor: ChargingPointDetailStatusColor[EStatusPill.Occupy],
          },
          {
            title: '無服務',
            value: sumConnectorDetail('unavailableCount'),
            backgroundColor:
              ChargingPointDetailStatusColor[EStatusPill.Unavailable],
          },
        ].map(item => (
          <div
            key={item.title}
            style={{
              backgroundColor: item.backgroundColor,
            }}
            className="flex justify-between items-center text-white h-25 p-10">
            <div className="mr-10">{item.title}</div>
            <div>{item.value}</div>
          </div>
        ))}
      </div>
      <div className="relative"></div>
      {false ? null : (
        <DetailPanel>
          <Row className="content">
            <Col span={12} className="text-avaliable">
              可使用
            </Col>
            <Col span={12} className="text-white">
              {sumConnectorDetail('availableCount')}
            </Col>
            <Col span={12} className="text-reserved">
              預定中
            </Col>
            <Col span={12} className="text-white">
              {sumConnectorDetail('reservedCount')}
            </Col>
            <Col span={12} className="text-charging">
              準備充電
            </Col>
            <Col span={12} className="text-white">
              {sumConnectorDetail('preparingCount')}
            </Col>
            <Col span={12} className="text-charging">
              充電中
            </Col>
            <Col span={12} className="text-white">
              {sumConnectorDetail('chargingCount')}
            </Col>
            <Col
              span={12}
              style={{
                color: ChargingPointDetailStatusColor[EStatusPill.Occupy],
              }}>
              占用中
            </Col>
            <Col span={12} className="text-white">
              {sumConnectorDetail('finishingCount')}
            </Col>
            <Col span={12} className="text-unavaliable">
              充電樁停止
            </Col>
            <Col span={12} className="text-white">
              {sumConnectorDetail('suspendedEVSECount')}
            </Col>
            <Col span={12} className="text-unavaliable">
              車輛停止
            </Col>
            <Col span={12} className="text-white">
              {sumConnectorDetail('suspendedEVCount')}
            </Col>
            <Col span={12} className="text-[#859FAA]">
              無服務
            </Col>
            <Col span={12} className="text-white">
              {sumConnectorDetail('unavailableCount')}
            </Col>
            <Col span={12} className="text-unavaliable">
              故障
            </Col>
            <Col span={12} className="text-white">
              {sumConnectorDetail('faultedCount')}
            </Col>
          </Row>
          <div className="summary flex gap-10 justify-between py-5">
            <div className="flex flex-col">
              <p className="text-sm text-white opacity-50">站點數</p>
              <p className="text-white">
                {toThousandSeparator(parkingLotData?.total)}
              </p>
            </div>
            <div className="flex flex-col items-center">
              <p className="text-sm text-white opacity-50">充電樁 | 槍數</p>
              <p className="text-white">
                {chargingpointCount}
                {' | '}
                {sumConnectorDetail('deviceCount')}
              </p>
            </div>
          </div>
        </DetailPanel>
      )}
    </StyledBoard>
  )
}

const DetailPanel = styled.div`
  top: 0px;
  left: 80px;
  position: absolute;
  z-index: 999;
  width: 200px;
  background: rgba(92, 99, 101, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  display: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  p {
    margin: 0;
  }
  > .summary {
    background-color: rgba(255, 255, 255, 0.1);
  }
  .desc {
    color: rgba(255, 255, 255, 0.5);
  }
  .content,
  .summary {
    padding: 10px 15px;
    .ant-col:nth-child(2n) {
      text-align: right;
    }
  }
`

const StyledBoard = styled.div`
  position: absolute;
  top: 53px;
  left: 24px;
  min-width: 72px;
  z-index: 999;
  pointer-events: auto;
  &:hover {
    ${DetailPanel} {
      display: block;
    }
  }
`
